import { CopyIcon, ExternalLink } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Separator } from "../../components/ui/separator";
import { Switch } from "../../components/ui/switch";
import { Operator } from "./model";
import { useEffect, useMemo, useState } from "react";
import { fetch } from "@/src/lib/fetch";
import { Link } from "react-router-dom";
import { useToast } from "../../components/ui/use-toast";
import { copyToClipboard } from "../../lib/utils";
import { YachtDetailsModal } from "../Offers/EditOffer/YachtDetailsModal";
import { extractFromMultiName } from "../Locations/utils";

export const OperatorDetailsModal = ({
  open,
  setOpen,
  id,
}: {
  open: boolean;
  setOpen: (open: boolean) => void;
  id: Operator["id"] | null;
}) => {
  const [operator, setOperator] = useState<Operator | null>(null);
  const [yachtId, setYachtId] = useState<string | null>(null);
  const [yachtModalOpen, setYachtModalOpen] = useState(false);

  const { toast } = useToast();

  useEffect(() => {
    async function getOperator() {
      await fetch<Operator>("/api/operators/operator/" + id + "?limit=100").then((res) => setOperator(res));
    }

    id && getOperator();
  }, [id]);

  function handleClipboard(text: string) {
    copyToClipboard(text);
    toast({
      title: "Copied!",
      variant: "default",
    });
  }

  const groupedLocations = useMemo(
    () =>
      operator?.locations.reduce(
        (acc, location) => {
          const country = extractFromMultiName(location.town?.country.names, "Unknown country");
          const existing = acc.find((item) => item.country === country);
          const locationName = extractFromMultiName(location.names);
          if (existing) {
            existing.names.push(locationName);
            existing.names.sort();
          } else {
            acc.push({ country, names: [locationName] });
          }
          return acc.sort((a, b) => a.country.localeCompare(b.country));
        },
        [] as { country: string; names: string[] }[],
      ),
    [operator?.locations],
  );

  if (!operator) return null;

  return (
    <Dialog onOpenChange={setOpen} open={open}>
      <DialogContent className="w-[720px] max-w-[720px] max-h-[calc(100vh-64px)] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-3xl">Operator details</DialogTitle>
        </DialogHeader>
        <Separator />
        <div className="flex flex-col gap-4 relative">
          <Button variant="link" className="absolute top-0 right-0" asChild>
            <Link to={`/operators/${operator.id}`}>Edit</Link>
          </Button>
          <div className="grid grid-cols-2 gap-4 text-base">
            <div className="flex flex-col gap-2">
              <span>Operator name</span>
              <span className="font-medium">{operator.name}</span>
            </div>
            <div className="flex flex-col gap-2">
              <span>Contact person</span>
              <span className="font-medium">{operator.contactPerson}</span>
            </div>
            <div className="flex flex-col gap-2">
              <span>E-mail</span>
              <span className="font-medium">{operator.email}</span>
            </div>
            <div className="flex flex-col gap-2">
              <span>E-mail #2 (post-booking communication)</span>
              <span className="font-medium">{operator.email2}</span>
            </div>
            <div className="flex flex-col gap-2">
              <span>Phone number</span>
              <span className="font-medium">{operator.phone}</span>
            </div>
            <div className="flex flex-col gap-2">
              <span>Website/domain</span>
              <span className="font-medium">{operator.website}</span>
            </div>
            <div className="flex flex-col gap-2">
              <span>Address</span>
              <span className="font-medium">{operator.address}</span>
            </div>
            <div className="flex flex-col gap-2">
              <span>Rating</span>
              <span className="font-medium">{operator.rating}</span>
            </div>
            <div className="flex flex-col gap-2">
              <span>Helm rating</span>
              <span className="font-medium">{operator.ratingHelm}</span>
            </div>
            <div className="flex flex-col gap-2">
              <span>Anchor rating</span>
              <span className="font-medium">{operator.ratingAnchor}</span>
            </div>
            <div className="flex flex-col gap-2">
              <span>Locations</span>
              <ul className="font-medium">
                {groupedLocations?.map((loc) => (
                  <li>
                    <span>{loc.country}</span>
                    <ul className="px-3">{loc.names?.map((name: string) => <li key={name}>{name}</li>)}</ul>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
        <Separator />
        <div className="flex flex-col gap-4 overflow-hidden">
          <h3 className="text-xl font-semibold">Yachts</h3>
          <div className="flex items-start gap-4 overflow-x-auto pb-3">
            {operator?.yachts?.map((yacht, index) => (
              <div
                className="cursor-pointer"
                onClick={() => {
                  setYachtId(yacht.id);
                  setYachtModalOpen(true);
                }}
              >
                <div key={index} className="flex flex-col min-w-[174px]">
                  <img
                    src={yacht.media?.[0]}
                    width={174}
                    className="w-[174px] h-[108px] rounded-[5px] mb-1 bg-gray-600 object-cover"
                  />
                  <span className="text-sm font-medium">"{yacht.name}"</span>
                  <span className="text-xs font-medium">
                    {yacht.modelName} | {yacht.year}
                  </span>
                </div>
              </div>
            ))}
          </div>
          <Button className="flex justify-start" variant="link">
            <Link className="flex items-center gap-2" to="/yachts">
              See all <ExternalLink />
            </Link>
          </Button>
        </div>
        <div className="flex flex-col gap-4 relative">
          <Button variant="link" className="absolute top-0 right-0" asChild>
            <Link to={`/operators/${operator.id}`}>Edit</Link>
          </Button>
          <h3 className="text-xl font-semibold">Yachts visibility</h3>
          <div className="flex flex-col gap-4">
            <span className="text-base text-slate-700 font-medium">Charter type</span>
            <div className="font-medium">TODO</div>
          </div>
          <Separator />
          <div className="grid grid-cols-3">
            <div className="flex flex-col gap-4">
              <span className="text-base text-slate-700 font-medium">APIs - Offer Creator</span>
              <div className="flex items-center gap-2">
                <Switch disabled checked={operator.visibility?.helm_app_ns === "visible"} /> NauSYS
              </div>
              <div className="flex items-center gap-2">
                <Switch disabled checked={operator.visibility?.helm_app_cya === "visible"} /> CYA (Central Yacht Agent)
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  disabled
                  checked={
                    operator.visibility?.helm_app_yf === "visible" || !!operator.visibility?.helm_app_yf_over_cya
                  }
                />{" "}
                Yacht Folio{operator.visibility?.helm_app_yf_over_cya ? " (over CYA)" : ""}
              </div>
              <div className="flex items-center gap-2">
                <Switch disabled checked={operator.visibility?.helm_app_ankor === "visible"} /> Ankor
              </div>
              <div className="flex items-center gap-2">
                <Switch disabled checked={operator.visibility?.helm_app_bm === "visible"} /> Booking Manager
              </div>
              <div className="flex items-center gap-2">
                <Switch disabled checked={operator.visibility?.helm_app_helm === "visible"} /> non-API yachts
              </div>
            </div>
            <div className="flex flex-col gap-4">
              <span className="text-base text-slate-700 font-medium">APIs - HELM Website</span>
              <div className="flex items-center gap-2">
                <Switch disabled checked={operator.visibility?.helm_ns === "visible"} /> NauSYS
              </div>
              <div className="flex items-center gap-2">
                <Switch disabled checked={operator.visibility?.helm_cya === "visible"} /> CYA (Central Yacht Agent)
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  disabled
                  checked={operator.visibility?.helm_yf === "visible" || !!operator.visibility?.helm_yf_over_cya}
                />{" "}
                Yacht Folio{operator.visibility?.helm_yf_over_cya ? " (over CYA)" : ""}
              </div>
              <div className="flex items-center gap-2">
                <Switch disabled checked={operator.visibility?.helm_ankor === "visible"} /> Ankor
              </div>
              <div className="flex items-center gap-2">
                <Switch disabled checked={operator.visibility?.helm_bm === "visible"} /> Booking Manager
              </div>
              <div className="flex items-center gap-2">
                <Switch disabled checked={operator.visibility?.helm_helm === "visible"} /> non-API yachts
              </div>
            </div>
            <div className="flex flex-col gap-4">
              <span className="text-base text-slate-700 font-medium">APIs - Anchor Website</span>
              <div className="flex items-center gap-2">
                <Switch disabled checked={operator.visibility?.anchor_ns === "visible"} /> NauSYS
              </div>
              <div className="flex items-center gap-2">
                <Switch disabled checked={operator.visibility?.anchor_cya === "visible"} /> CYA (Central Yacht Agent)
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  disabled
                  checked={operator.visibility?.anchor_yf === "visible" || !!operator.visibility?.anchor_yf_over_cya}
                />{" "}
                Yacht Folio{operator.visibility?.anchor_yf_over_cya ? " (over CYA)" : ""}
              </div>
              <div className="flex items-center gap-2">
                <Switch disabled checked={operator.visibility?.anchor_ankor === "visible"} /> Ankor
              </div>
              <div className="flex items-center gap-2">
                <Switch disabled checked={operator.visibility?.anchor_bm === "visible"} /> Booking Manager
              </div>
              <div className="flex items-center gap-2">
                <Switch disabled checked={operator.visibility?.anchor_helm === "visible"} /> non-API yachts
              </div>
            </div>
            <p className="text-sm text-gray-600 mt-2">
              non-API yachts (manually added) must always be visible in the offer creating process.
            </p>
          </div>
        </div>
        <Separator />
        <div className="flex flex-col gap-4 relative">
          <Button variant="link" className="absolute top-0 right-0" asChild>
            <Link to={`/operators/${operator.id}`}>Edit</Link>
          </Button>
          <h3 className="text-xl font-semibold">Bank transfer details</h3>
          <div className="grid grid-cols-4 gap-6">
            <div className="flex flex-col gap-2 text-base">
              <span>Recipent name</span>
              <span
                onClick={(e) => handleClipboard(e.currentTarget.innerText)}
                className="font-medium flex items-center cursor-pointer"
              >
                {operator.transferDetails?.recipientName} <CopyIcon size={12} className="stroke-slate-500 min-w-6" />
              </span>
            </div>
            <div className="flex flex-col gap-2 text-base">
              <span>BIC/SWIFT</span>
              <span
                onClick={(e) => handleClipboard(e.currentTarget.innerText)}
                className="font-medium flex items-center cursor-pointer"
              >
                <span className="line-clamp-1 break-words">{operator.transferDetails?.accountNumber}</span>
                <CopyIcon size={12} className="stroke-slate-500 min-w-6" />
              </span>
            </div>
            <div className="flex flex-col gap-2 text-base">
              <span>IBAN</span>
              <span className="font-medium">TODO</span>
            </div>
            <div className="flex flex-col gap-2 text-base">
              <span>Currency</span>
              <span className="font-medium">{operator.transferDetails?.currencyCode}</span>
            </div>
          </div>
        </div>
        <Separator />
        <div className="flex flex-col gap-4 relative">
          <Button variant="link" className="absolute top-0 right-0" asChild>
            <Link to={`/operators/${operator.id}`}>Edit</Link>
          </Button>
          <h3 className="text-xl font-semibold">Discounts & revenues</h3>
          <div className="grid grid-cols-2">
            <div className="flex flex-col gap-2 text-base">
              <span>Commision fee</span>
              <span className="font-medium">
                {operator.commission}
                {operator.commissionType === "percentage" ? "%" : ""}
              </span>
            </div>
            <div className="flex flex-col gap-2 text-base">
              <span>Custom discount</span>
              <span className="font-medium">{operator.customDiscount}%</span>
            </div>
          </div>
        </div>
        <div className="flex flex-col gap-4 relative">
          <Button variant="link" className="absolute top-0 right-0" asChild>
            <Link to={`/operators/${operator.id}`}>Edit</Link>
          </Button>
          <h3 className="text-lg font-semibold">Payment schedule</h3>
          <div className="grid grid-cols-3 gap-4">
            <div className="flex flex-col gap-2 text-base">
              <span className="font-medium">First payment</span>
              <div className="p-4 rounded-[8px] bg-slate-50 border border-blue-100 flex flex-col gap-2">TODO</div>
            </div>
          </div>
        </div>
        <Separator />
        <div className="flex flex-col gap-4 relative">
          <Button variant="link" className="absolute top-0 right-0" asChild>
            <Link to={`/operators/${operator.id}`}>Edit</Link>
          </Button>
          <h3 className="text-xl font-semibold">Terms & conditions</h3>
          {operator.termsAndConditions?.url ? (
            <Button className="flex items-center gap-2 justify-start" variant={"link"} asChild>
              <Link to={operator.termsAndConditions?.url} target="_blank">
                {operator.termsAndConditions?.name} <ExternalLink size={16} />
              </Link>
            </Button>
          ) : (
            "No data"
          )}
        </div>
        <Separator />
        <div className="flex flex-col gap-4 relative">
          <Button variant="link" className="absolute top-0 right-0" asChild>
            <Link to={`/operators/${operator.id}`}>Edit</Link>
          </Button>
          <h3 className="text-xl font-semibold">Good to know</h3>
          {operator.goodToKnow.type === "simple" && (
            <div>
              <p className="text-sm text-slate-700">{operator.goodToKnow.simple}</p>
            </div>
          )}
          {operator.goodToKnow.type === "charter-dependent" && (
            <>
              <h4 className="text-base text-slate-700 font-semibold">Section depends on Charter type</h4>
              <div className="grid grid-cols-2 gap-4">
                {operator.goodToKnow.bareboat && (
                  <div className="flex flex-col gap-2">
                    <h5 className="text-base font-medium">Bareboat</h5>
                    <p className="text-sm text-slate-700">{operator.goodToKnow.bareboat}</p>
                  </div>
                )}
                {operator.goodToKnow.skippered && (
                  <div className="flex flex-col gap-2">
                    <h5 className="text-base font-medium">Skippered</h5>
                    <p className="text-sm text-slate-700">{operator.goodToKnow.skippered}</p>
                  </div>
                )}
                {operator.goodToKnow.crewed && (
                  <div className="flex flex-col gap-2">
                    <h5 className="text-base font-medium">Crewed</h5>
                    <p className="text-sm text-slate-700">{operator.goodToKnow.crewed}</p>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
        <div className="flex flex-col gap-4 relative">
          <Button variant="link" className="absolute top-0 right-0" asChild>
            <Link to={`/operators/${operator.id}`}>Edit</Link>
          </Button>
          <h3 className="text-xl font-semibold">Boarding information</h3>
          <div className="flex flex-col gap-4">
            {operator?.boardingInfos?.map((info, index) => (
              <div key={index} className="grid grid-cols-1 gap-4 bg-slate-50 rounded-[8px] p-4 border border-blue-100">
                <div className="flex flex-col gap-2">
                  <span className="text-base font-medium">{info.location?.names}</span>
                  {info.file?.url && (
                    <Button className="justify-start !px-0 flex gap-2" variant="link" asChild>
                      <Link target="_blank" to={info.file?.url}>
                        {info.file?.name} <ExternalLink size={16} />
                      </Link>
                    </Button>
                  )}
                </div>
                <p className="text-sm break-all">{info.description}</p>
              </div>
            ))}
            {!operator?.boardingInfos?.length && "No data"}
          </div>
        </div>
        <Separator />
        <div className="flex flex-col gap-4 relative">
          <Button variant="link" className="absolute top-0 right-0" asChild>
            <Link to={`/operators/${operator.id}`}>Edit</Link>
          </Button>
          <h3 className="text-xl font-semibold">Broker notes</h3>
          <p className="text-base">{operator.notes ? operator.notes : "No data"}</p>
        </div>
        <Separator />
        <Button className="flex justify-start" variant="link" asChild>
          <Link to="/bookings" className="flex items-center gap-2">
            Our bookings <ExternalLink size={20} />
          </Link>
        </Button>
        <DialogFooter>
          <Button asChild>
            <Link to={`/operators/${operator.id}`}>Edit</Link>
          </Button>
        </DialogFooter>
        {yachtModalOpen && (
          <YachtDetailsModal yachtDetailsId={yachtId} open={yachtModalOpen} setOpen={setYachtModalOpen} />
        )}
      </DialogContent>
    </Dialog>
  );
};
